/**
 * Centralized chart color palette.
 *
 * Reads daisyUI / @theme CSS custom properties at runtime so chart colors
 * automatically follow the active theme.  Call `getChartColors()` inside
 * `$effect` (i.e. browser-only code) to get the resolved values.
 *
 * For colors that have no matching CSS variable (tooltip chrome, grid lines,
 * hover-label outlines) we keep sensible dark-mode defaults that pair well
 * with any daisyUI dark theme.
 */

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Read a CSS custom-property value from :root, trimmed. */
function cssVar(name: string): string {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/**
 * Turn any CSS color the browser understands into an `rgba(r, g, b, alpha)`
 * string.  Works by painting the color onto a 1×1 canvas and reading back
 * the pixel data — this handles hex, oklch, hsl, named colors, etc.
 */
function colorWithAlpha(cssColor: string, alpha: number): string {
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 1;
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = cssColor;
    ctx.fillRect(0, 0, 1, 1);
    const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface ChartColors {
    // Brand / semantic
    brandOrange: string;
    brandBlue: string;
    brandRed: string;
    brandOrangeTranslucent: string;

    // Role
    roleTank: string;
    roleDamage: string;
    roleSupport: string;

    // Text / labels
    axisLabel: string;
    text: string;
    textMuted: string;

    // Tooltip
    tooltipBg: string;
    tooltipBorder: string;
    tooltipText: string;

    // Grid / axis lines
    axisLine: string;
    splitLine: string;
    splitLineLight: string;

    // Canvas helpers
    circleFill: string;

    // Hover label
    hoverLabelText: string;
    hoverLabelBorder: string;
}

/**
 * Resolved map-type colors for ECharts.
 * Mirrors the `--color-mode-*` CSS tokens from @theme so chart bars
 * match the badge/accent palette used on the maps pages.
 */
export interface MapTypeChartColors {
    control: string;
    escort: string;
    hybrid: string;
    push: string;
    flashpoint: string;
    clash: string;
}

/**
 * Resolve per-map-type colors from CSS custom properties.
 *
 * **Must be called in the browser** (inside `$effect`, event
 * handlers, etc.) because it uses `getComputedStyle`.
 */
export function getMapTypeChartColors(): MapTypeChartColors {
    return {
        control: cssVar("--color-mode-control") || "#00b5d8",
        escort: cssVar("--color-mode-escort") || "#eab308",
        hybrid: cssVar("--color-mode-hybrid") || "#36d399",
        push: cssVar("--color-mode-push") || "#a78bfa",
        flashpoint: cssVar("--color-mode-flashpoint") || "#f87171",
        clash: cssVar("--color-mode-clash") || "#fb923c",
    };
}

/**
 * Resolve chart colors from CSS custom properties.
 *
 * **Must be called in the browser** (inside `$effect`, event
 * handlers, etc.) because it uses `getComputedStyle`.
 */
export function getChartColors(): ChartColors {
    // Theme-aware values read from CSS vars
    const primary = cssVar("--color-primary") || "#f99e1a";
    const secondary = cssVar("--color-secondary") || "#3a7bca";
    const baseContent = cssVar("--color-base-content") || "#a6adbb";
    const base100 = cssVar("--color-base-100") || "#1d232a";

    // Derived with alpha
    const baseContentAlpha = (a: number) => colorWithAlpha(baseContent, a);
    const primaryAlpha = (a: number) => colorWithAlpha(primary, a);

    return {
        // ── Brand / semantic ───────────────────────
        brandOrange: primary,
        brandBlue: secondary,
        // Use the theme's error color when present so the ban-rate trend
        // line matches the BR badges and "menace" color cues used elsewhere.
        brandRed: cssVar("--color-error") || "#e74c3c",
        brandOrangeTranslucent: primaryAlpha(0.35),

        // ── Role colors (custom @theme vars) ───────
        roleTank: cssVar("--color-role-tank") || "#5b9bd5",
        roleDamage: cssVar("--color-role-damage") || "#e06666",
        roleSupport: cssVar("--color-role-support") || "#6aa84f",

        // ── Text / labels ──────────────────────────
        axisLabel: baseContent,
        text: baseContentAlpha(0.87),
        textMuted: baseContentAlpha(0.7),

        // ── Tooltip ────────────────────────────────
        tooltipBg: "rgba(30, 30, 40, 0.95)",
        tooltipBorder: baseContentAlpha(0.1),
        tooltipText: baseContentAlpha(0.87),

        // ── Grid / axis lines ──────────────────────
        axisLine: baseContentAlpha(0.15),
        splitLine: baseContentAlpha(0.09),
        splitLineLight: baseContentAlpha(0.12),

        // ── Canvas helpers ─────────────────────────
        circleFill: base100,

        // ── Hover label ────────────────────────────
        hoverLabelText: "#fff",
        hoverLabelBorder: "rgba(0, 0, 0, 0.8)",
    };
}
